import { useEffect, useMemo } from "react";

import { Box, Spacer, Stack } from "@chakra-ui/react";

import { OrderModel } from "api";
import LoadingSpinner from "components/LoadingSpinner";
import SearchBoxWithIcon from "components/SearchBoxWithIcon";
import { ActiveSort, useRequestParams } from "contexts/RequestParamsProvider";
import EditOrderForm from "forms/EditOrderForm";
import useContractDetails from "hooks/api/contracts/useContractDetails";
import useOrders from "hooks/api/orders/useOrders";

import { removeNullable } from "services/apiHelpers";
import { detailsBodyPadding, mobileStyleBreakpoint } from "util/styleHelpers";
import OrdersList from "./OrdersList";
import { ordersColumns } from "./OrdersTable";
import ContractDetailsFiltersSection, {
  detailsTabFilterCategories,
  FilterSectionStickyHeader,
} from "../filters/ContractDetailsFiltersSection";
import ContractDetailsSelectsProvider from "contexts/ContractDetailsSelectsProvider";

export const requestOrderButtonPadding = mobileStyleBreakpoint("16px", "48px");

const EditOrderTab = ({
  toggleIndicator,
  setErrorMessage,
  filters: filterDefinitions,
}: DetailsTabProps) => {
  const { filters, searchTerm, onChangeSearchTerm, activeSort } =
    useRequestParams();

  const { data: contract, error: contractDetailsError } = useContractDetails();

  useEffect(() => {
    toggleIndicator?.(contract?.moreOrdersRequired ?? false);
  }, [toggleIndicator, contract]);

  useEffect(() => {
    if (contractDetailsError && setErrorMessage) {
      setErrorMessage((contractDetailsError as Error).message);
    }
  }, [setErrorMessage, contractDetailsError]);

  const {
    data: orders,
    isLoading,
    isFetching,
    error: ordersListError,
  } = useOrders(
    contract?.contractNumber,
    searchTerm,
    filters as OrdersFilters,
    activeSort as ActiveSort<OrderModel>
  );

  useEffect(() => {
    if (ordersListError && setErrorMessage) {
      setErrorMessage((ordersListError as Error).message);
    }
  }, [setErrorMessage, ordersListError]);

  const selectValues = useMemo(
    () =>
      orders?.map((value) => ({
        shipTo: value.shipTo as string | number,
        shipFrom: {
          label: value.shipFrom,
          value: value.shipFromCode,
        } as SelectOption,
        mode: {
          label: (value.mode as Record<string, string | number>).name,
          value: (value.mode as Record<string, string | number>).code,
        } as SelectOption,
      })),
    [orders]
  );

  return (
    <ContractDetailsSelectsProvider
      values={selectValues ?? []}
      selectMetadata={detailsTabFilterCategories}
    >
      <Stack spacing={6} pt={detailsBodyPadding}>
        <FilterSectionStickyHeader>
          <Box>
            <SearchBoxWithIcon
              value={searchTerm}
              onChange={onChangeSearchTerm}
            />
          </Box>
          <Spacer />
          <ContractDetailsFiltersSection
            filterDefinitions={filterDefinitions}
            sortOptions={removeNullable(
              ordersColumns.map((column) =>
                column.sortKey && column.columnLabel
                  ? {
                      label: column.columnLabel,
                      sortKey: column.sortKey,
                    }
                  : null
              )
            )}
          />
        </FilterSectionStickyHeader>
        {!orders || !contract || isFetching || isLoading ? (
          <LoadingSpinner />
        ) : (
          <EditOrderForm
            customerCode={contract.customerCode}
            customerName={contract.customerName}
          >
            <OrdersList orders={orders} />
          </EditOrderForm>
        )}
      </Stack>
    </ContractDetailsSelectsProvider>
  );
};

export default EditOrderTab;
